import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { notificationsApi } from '../../services/api';
import { notificationKeys } from '../../lib/queryKeys';
import {
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
} from '../../hooks/mutations';
import { useNotifications } from '../../contexts/NotificationsContext';
import { NotificationItem } from '../../components/ui/NotificationItem';

const LIMIT = 50;

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const { refresh } = useNotifications();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();

  const query = useInfiniteQuery({
    queryKey: [...notificationKeys.lists(), 'paginated', filter],
    queryFn: ({ pageParam = 0 }) => {
      const params = { limit: LIMIT, skip: pageParam };
      if (filter === 'unread') params.is_read = false;
      return notificationsApi.getAll(params).then((r) => r.data);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = lastPage?.notifications || [];
      if (fetched.length < LIMIT) return undefined;
      return allPages.reduce((acc, p) => acc + (p?.notifications?.length || 0), 0);
    },
  });

  const notifications = useMemo(
    () => (query.data?.pages || []).flatMap((p) => p?.notifications || []),
    [query.data]
  );
  const loading = query.isPending;
  const loadingMore = query.isFetchingNextPage;
  const hasMore = !!query.hasNextPage;

  const handleMarkRead = async (id) => {
    await markRead.mutateAsync(id);
    refresh();
  };

  const handleDelete = async (id) => {
    await deleteNotification.mutateAsync(id);
    refresh();
  };

  const handleMarkAllRead = async () => {
    await markAllRead.mutateAsync();
    refresh();
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="mx-auto w-full max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h1 className="hidden md:block text-lg font-bold text-foreground">{t('notifications.title')}</h1>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="ml-auto flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <CheckCheck size={14} />
            {t('notifications.mark_all_read')}
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 bg-muted/40 rounded-lg p-1 w-fit">
        {[{ key: 'all', label: t('common.all') }, { key: 'unread', label: t('notifications.unread') }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === key ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-muted-foreground" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Bell size={40} className="mb-3 opacity-30" />
          <p className="text-sm">
            {filter === 'unread' ? t('notifications.no_unread') : t('notifications.no_notifications')}
          </p>
        </div>
      ) : (
        <div className="space-y-0.5">
          {notifications.map(n => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              onNavigate={navigate}
            />
          ))}
        </div>
      )}

      {/* Load more */}
      {!loading && hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => query.fetchNextPage()}
            disabled={loadingMore}
            className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            {loadingMore && <Loader2 size={14} className="animate-spin" />}
            {t('notifications.load_more')}
          </button>
        </div>
      )}
    </div>
  );
}
